import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ShoppingCart, LogOut, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminOrders = () => {
  const { logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewOrder = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-bold text-lg tracking-tight">
              G.O.A.T. Admin
            </span>
            <nav className="hidden md:flex gap-4">
              <Link
                to="/admin/dashboard"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Products
              </Link>
              <Link
                to="/admin/orders"
                className="text-white font-medium text-sm uppercase tracking-wider"
              >
                Orders
              </Link>
              <Link
                to="/admin/subscribers"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Subscribers
              </Link>
              <Link
                to="/admin/account"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                My Account
              </Link>
            </nav>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl uppercase tracking-tight flex items-center gap-3">
            <ShoppingCart className="w-6 h-6" />
            Orders
          </h1>
          <p className="text-zinc-500 mt-1">{orders.length} orders</p>
        </div>

        {/* Orders Table */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-zinc-400" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            No orders yet
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-hidden" data-testid="orders-table">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Order ID</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider hidden md:table-cell">Customer</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider hidden md:table-cell">Date</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Total</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {orders.map(order => (
                  <tr key={order.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-4 font-mono text-sm">
                      {order.id.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div>
                        <p className="font-medium text-sm">
                          {order.customerFirstName} {order.customerLastName}
                        </p>
                        <p className="text-zinc-500 text-xs">{order.customerEmail}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-600 hidden md:table-cell">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-4 font-bold">
                      ${order.totalAmount.toFixed(2)}
                    </td>
                    <td className="px-4 py-4">
                      {order.paymentStatus === 'paid' ? (
                        <span className="inline-block text-xs font-bold text-green-700 bg-green-100 px-2 py-1 uppercase">
                          Paid
                        </span>
                      ) : order.paymentStatus === 'failed' ? (
                        <span className="inline-block text-xs font-bold text-red-700 bg-red-100 px-2 py-1 uppercase">
                          Failed
                        </span>
                      ) : (
                        <span className="inline-block text-xs font-bold text-yellow-700 bg-yellow-100 px-2 py-1 uppercase">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewOrder(order)}
                        data-testid={`view-order-${order.id}`}
                      >
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Order Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Order Details
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Order Info */}
              <div className="space-y-2 text-sm">
                <p><span className="text-zinc-500">Order ID:</span> <span className="font-mono">{selectedOrder.id}</span></p>
                <p><span className="text-zinc-500">Date:</span> {formatDate(selectedOrder.createdAt)}</p>
                <p>
                  <span className="text-zinc-500">Status:</span>{' '}
                  <span className={`font-bold uppercase ${
                    selectedOrder.paymentStatus === 'paid' 
                      ? 'text-green-600' 
                      : selectedOrder.paymentStatus === 'failed'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                  }`}>
                    {selectedOrder.paymentStatus}
                  </span>
                </p>
              </div>

              {/* Customer */}
              <div>
                <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Customer</h3>
                <p className="font-medium">{selectedOrder.customerFirstName} {selectedOrder.customerLastName}</p>
                <p className="text-zinc-600 text-sm">{selectedOrder.customerEmail}</p>
                {selectedOrder.customerPhone && (
                  <p className="text-zinc-600 text-sm">{selectedOrder.customerPhone}</p>
                )}
              </div>

              {/* Delivery Method */}
              <div>
                <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Delivery Method</h3>
                {selectedOrder.deliveryMethod === 'pickup' ? (
                  <div className="bg-amber-50 border border-amber-200 p-3">
                    <p className="font-medium text-amber-800">🏪 Local Pickup</p>
                    <p className="text-amber-700 text-sm mt-1">Pickup will be arranged separately.</p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 p-3">
                    <p className="font-medium text-blue-800">📦 Shipping</p>
                    {selectedOrder.shippingStreet && (
                      <p className="text-blue-700 text-sm mt-1">
                        {selectedOrder.shippingStreet}<br />
                        {selectedOrder.shippingSuburb}, {selectedOrder.shippingState} {selectedOrder.shippingPostcode}<br />
                        {selectedOrder.shippingCountry}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Items */}
              <div>
                <h3 className="font-bold text-sm uppercase tracking-wider mb-2">Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center bg-zinc-50 p-3">
                      <div>
                        <p className="font-medium text-sm">{item.productName}</p>
                        <p className="text-zinc-500 text-xs">
                          {item.size && `Size: ${item.size}`}
                          {item.size && item.colour && ' / '}
                          {item.colour && `Colour: ${item.colour}`}
                          {' × '}{item.quantity}
                        </p>
                      </div>
                      <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-zinc-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Subtotal</span>
                  <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                {selectedOrder.appliedDiscountCode && selectedOrder.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-[#00cc7d]">
                    <span>Discount ({selectedOrder.appliedDiscountCode})</span>
                    <span>-${selectedOrder.discountAmount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Shipping</span>
                  <span>${selectedOrder.shippingCost?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-zinc-200">
                  <span>Total</span>
                  <span>${selectedOrder.totalAmount?.toFixed(2)} AUD</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
